import type { SchedulingJobWeekStatusResponse } from '../../../api/generated';
import { SchedulingJobWeekStatusResponseStatusEnum } from '../../../api/generated';
import type { WeekCalculationDisplayStatus, WeekCalculationState } from './types';

// What clicking a week's status chip should do.
export type StatusChipAction = 'none' | 'recalculate' | 'retry';

/**
 * Which action a week's status chip offers.
 *
 * Takes only the status because it already encodes the calendar: `resolveWeekCalculationState` returns
 * LOCKED for a started week before looking at anything else, so LOCKED and "has started" are the same
 * condition here.
 *
 * Exhaustive without a `never` guard — every case returns, so adding a status fails to compile on the
 * missing return path rather than at runtime.
 */
export const resolveStatusChipAction = (
  status: WeekCalculationDisplayStatus
): StatusChipAction => {
  switch (status) {
    case 'LOCKED':
      // The week's plan is frozen and the backend rejects a calculation for it, so offering the
      // action would only produce an error dialog.
      return 'none';
    case 'UNKNOWN':
      // Nothing is known about this week. Loading the statuses is what the planner actually needs;
      // solving blind could also supersede a run already in progress that they cannot see.
      return 'retry';
    case 'CALCULATED':
    case 'CALCULATING':
    case 'NOT_CALCULATED':
    case 'FAILED':
      return 'recalculate';
  }
};

// The payload-free states are shared instances: nothing distinguishes two of them, and a stable
// identity lets `computeWeekKpi`'s memoised callers skip work.
const LOCKED: WeekCalculationState = { status: 'LOCKED' };
const NOT_CALCULATED: WeekCalculationState = { status: 'NOT_CALCULATED' };
const CALCULATING: WeekCalculationState = { status: 'CALCULATING' };
const UNKNOWN: WeekCalculationState = { status: 'UNKNOWN' };

// Builds the display state for a job of one particular status.
type WeekCalculationStateBuilder = (job: SchedulingJobWeekStatusResponse) => WeekCalculationState;

/**
 * How each backend job status maps to a display state.
 *
 * A `Record` keyed by the enum is the exhaustiveness check: a status added to the backend enum fails
 * to compile here until it is mapped. Waiting and Running collapse to the same state because queued
 * and solving are the same wait to a planner; the distinction is worker-fleet detail. Cancelled is
 * filtered out server-side, so it is unreachable in practice — mapped rather than omitted to keep the
 * check exhaustive, and to "not calculated" because a cancelled job was superseded before it ran.
 */
const STATE_BY_JOB_STATUS: Record<
  SchedulingJobWeekStatusResponseStatusEnum,
  WeekCalculationStateBuilder
> = {
  [SchedulingJobWeekStatusResponseStatusEnum.Waiting]: () => CALCULATING,
  [SchedulingJobWeekStatusResponseStatusEnum.Running]: () => CALCULATING,
  [SchedulingJobWeekStatusResponseStatusEnum.Completed]: (job) => ({
    status: 'CALCULATED',
    finishedAt: job.finishedAt ?? null
  }),
  [SchedulingJobWeekStatusResponseStatusEnum.Failed]: (job) => ({
    status: 'FAILED',
    finishedAt: job.finishedAt ?? null,
    errorMessage: job.errorMessage ?? null
  }),
  [SchedulingJobWeekStatusResponseStatusEnum.Cancelled]: () => NOT_CALCULATED
};

/**
 * Resolves what a week's status icon should show.
 *
 * `job` is the newest non-cancelled scheduling job for the week, or undefined when the week has
 * never been calculated (the backend omits such weeks entirely). Because the newest job wins
 * regardless of outcome, a failed retry reports FAILED even when an older successful result still
 * exists and is still what the week view renders — see
 * `rehabplanner-backend/docs/adr/0003-week-calculation-status-endpoint.md`.
 *
 * A started week is LOCKED whatever its job says: its plan is frozen, so no calculation can be
 * triggered and the status can never change again.
 *
 * `isUnavailable` says the statuses could not be fetched. A week with no job then reports UNKNOWN
 * rather than NOT_CALCULATED, because we have no grounds to claim it was never calculated. It is
 * checked *after* `job` so a week the planner just triggered still shows as calculating: that mark
 * is local knowledge and does not depend on the fetch having worked.
 */
export const resolveWeekCalculationState = (
  job: SchedulingJobWeekStatusResponse | undefined,
  isLocked: boolean,
  isUnavailable = false
): WeekCalculationState => {
  if (isLocked) return LOCKED;
  if (!job) return isUnavailable ? UNKNOWN : NOT_CALCULATED;

  // The map is exhaustive over the *generated* enum, but this value crossed a network boundary: a
  // status added to the backend reaches a client generated before it, and then has no entry. Reporting
  // UNKNOWN degrades one chip; throwing from here would take out the whole year plan, since this runs
  // inside the header's render.
  const toState = STATE_BY_JOB_STATUS[job.status] as WeekCalculationStateBuilder | undefined;

  return toState ? toState(job) : UNKNOWN;
};
